import { useState } from 'react';
import { tasksAPI, uploadFile, deleteAttachment } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MultiSelect } from './MultiSelect';
import { X, Copy, Film, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PendingFilesList, validateFiles, validateMediaFiles } from './FileFields';
import { AttachmentGallery } from './AttachmentGallery';
import { DescriptionMedia } from './DescriptionMedia';
import { ChecklistEditor, checklistOps } from './ChecklistEditor';
import { CommentsSection } from './CommentsSection';
import { ReleaseSummarySection } from './ReleaseSummarySection';
import { TASK_TYPE_COLORS, TASK_TYPE_OPTIONS } from '../lib/taskTypes';
import { DEV_STATUSES } from '../lib/taskStatuses';

const STATUS_OPTIONS = DEV_STATUSES;

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High', color: '#DC2626' },
  { value: 'medium', label: 'Medium', color: '#F59E0B' },
  { value: 'low', label: 'Low', color: '#0EA5E9' },
];

function TitleDescriptionFields({ formData, setFormData, canEdit }) {
  return (
    <>
      <div>
        <Label>Title</Label>
        {canEdit ? (
          <Input value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} placeholder="Task title" data-testid="dev-task-title-input" />
        ) : (
          <p className="text-sm font-medium text-[#111111] mt-1">{formData.title}</p>
        )}
      </div>
      <div>
        <Label>Description <span className="text-[#999999] font-normal">(optional)</span></Label>
        {canEdit ? (
          <Textarea value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} rows={4} data-testid="dev-task-description" />
        ) : (
          <p className="text-sm text-[#666666] mt-1 whitespace-pre-wrap">{formData.description || 'No description'}</p>
        )}
      </div>
    </>
  );
}

function TypePriorityStatusFields({ formData, setFormData, canEdit }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <Label>Type</Label>
        <Select value={formData.task_type} onValueChange={v => setFormData(f => ({ ...f, task_type: v }))} disabled={!canEdit}>
          <SelectTrigger data-testid="dev-task-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TASK_TYPE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`dev-task-type-option-${opt.value}`}>
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />{opt.label}</div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Priority</Label>
        <Select value={formData.priority} onValueChange={v => setFormData(f => ({ ...f, priority: v }))} disabled={!canEdit}>
          <SelectTrigger data-testid="dev-task-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PRIORITY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />{opt.label}</div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Status</Label>
        <Select value={formData.status} onValueChange={v => setFormData(f => ({ ...f, status: v }))}>
          <SelectTrigger data-testid="dev-task-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ModuleAssigneeFields({ formData, setFormData, canEdit, task, modules, users }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Module</Label>
        {canEdit ? (
          <Select value={formData.module_id} onValueChange={v => setFormData(f => ({ ...f, module_id: v }))}>
            <SelectTrigger data-testid="dev-task-module"><SelectValue placeholder="Select module" /></SelectTrigger>
            <SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <p className="text-sm text-[#666666] mt-1">{task?.module_name || '—'}</p>
        )}
      </div>
      <div>
        <Label>Assignees</Label>
        {canEdit ? (
          <MultiSelect
            options={users}
            selected={formData.assignee_ids || []}
            onChange={(ids) => setFormData(f => ({ ...f, assignee_ids: ids }))}
            placeholder="Unassigned"
            testId="dev-task-assignee"
          />
        ) : (
          <p className="text-sm text-[#666666] mt-1">{task?.assignee_name || 'Unassigned'}</p>
        )}
      </div>
    </div>
  );
}

function LinkDeadlineFields({ formData, setFormData, canEdit }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Link <span className="text-[#999999] font-normal">(optional)</span></Label>
        {canEdit ? (
          <Input value={formData.link} onChange={e => setFormData(f => ({ ...f, link: e.target.value }))} placeholder="https://..." data-testid="dev-task-link" />
        ) : formData.link ? (
          <a href={formData.link} target="_blank" rel="noreferrer" className="text-sm text-[#FF6B35] underline mt-1 block truncate">{formData.link}</a>
        ) : (
          <p className="text-sm text-[#666666] mt-1">No link</p>
        )}
      </div>
      <div>
        <Label>Deadline <span className="text-[#999999] font-normal">(optional)</span></Label>
        {canEdit ? (
          <Input
            type="date"
            value={formData.deadline || ''}
            onChange={e => setFormData(f => ({ ...f, deadline: e.target.value || null }))}
            data-testid="dev-task-deadline"
          />
        ) : (
          <p className="text-sm text-[#666666] mt-1">
            {formData.deadline ? new Date(formData.deadline + 'T00:00:00').toLocaleDateString() : 'No deadline'}
          </p>
        )}
      </div>
    </div>
  );
}

function RestaurantRequestedFields({ formData, setFormData, canEdit, task, restaurants }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Restaurant <span className="text-[#999999] font-normal">(optional)</span></Label>
        {canEdit ? (
          <Select value={formData.restaurant_id || 'none'} onValueChange={v => setFormData(f => ({ ...f, restaurant_id: v === 'none' ? '' : v }))}>
            <SelectTrigger data-testid="dev-task-restaurant"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm text-[#666666] mt-1">{task?.restaurant_name || '—'}</p>
        )}
      </div>
      <div>
        <Label>Requested by <span className="text-[#999999] font-normal">(optional)</span></Label>
        {canEdit ? (
          <Input value={formData.requested_by} onChange={e => setFormData(f => ({ ...f, requested_by: e.target.value }))} placeholder="Who requested this" data-testid="dev-task-requested-by" />
        ) : (
          <p className="text-sm text-[#666666] mt-1">{formData.requested_by || '—'}</p>
        )}
      </div>
    </div>
  );
}

function AttachmentSection({ task, canEdit, isAdmin, pendingFiles, onAddFiles, onRemovePending, onDeleteAttachment }) {
  return (
    <div>
      <Label>Attachments <span className="text-[#999999] font-normal">(optional)</span></Label>
      <AttachmentGallery attachments={task?.attachments || []} canDelete={isAdmin} onDelete={onDeleteAttachment} />
      <PendingFilesList pendingFiles={pendingFiles} onRemovePending={onRemovePending} />
      {canEdit && (
        <Input type="file" multiple onChange={e => { validateFiles(e.target.files, onAddFiles); e.target.value = ''; }} className="mt-2" data-testid="dev-task-file-input" />
      )}
    </div>
  );
}

function DescriptionMediaSection({ task, canEdit, isAdmin, pendingMedia, onAddMedia, onRemovePending, onDeleteMedia }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5">
        <ImageIcon size={14} /> Media in description
        <span className="text-[#999999] font-normal">(photos / videos shown inline)</span>
      </Label>
      <DescriptionMedia media={task?.description_media || []} canDelete={isAdmin} onDelete={onDeleteMedia} />
      {pendingMedia.length > 0 && (
        <div className="mt-2 space-y-1">
          {pendingMedia.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-[#FFF9E6] rounded px-2 py-1.5 text-sm" data-testid={`pending-media-${i}`}>
              <span className="flex items-center gap-1.5 truncate text-[#111111]">
                <Film size={13} /> {f.name} <span className="text-xs text-[#999999]">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
              </span>
              <button type="button" onClick={() => onRemovePending(i)} className="text-[#999999] hover:text-[#DC2626]" data-testid={`remove-pending-media-${i}`}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <Input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={e => { validateMediaFiles(e.target.files, onAddMedia); e.target.value = ''; }}
          className="mt-2"
          data-testid="dev-task-description-media-input"
        />
      )}
    </div>
  );
}

function TaskTypeBadge({ taskType }) {
  const opt = TASK_TYPE_OPTIONS.find(o => o.value === taskType) || TASK_TYPE_OPTIONS[1];
  const Icon = opt.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: `${opt.color}15`, color: opt.color }}
      data-testid={`task-type-badge-${taskType}`}
    >
      <Icon size={11} /> {opt.label}
    </span>
  );
}

export default function DevTaskModal({ task, modules, users, restaurants = [], isAdmin, currentUser, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingMedia, setPendingMedia] = useState([]);
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'medium',
    status: task?.status || 'todo',
    module_id: task?.module_id || '',
    assignee_ids: task?.assignee_ids || (task?.assignee_id ? [task.assignee_id] : []),
    link: task?.link || '',
    restaurant_id: task?.restaurant_id || '',
    requested_by: task?.requested_by || '',
    checklist: task?.checklist || [],
    task_type: task?.task_type || 'feature',
    deadline: task?.deadline || null,
  });

  const canEdit = isAdmin;
  const isEdit = !!task?.id;

  const setChecklist = (fn) => setFormData(f => ({ ...f, checklist: fn(f.checklist) }));

  const uploadPending = async (taskId) => {
    for (const f of pendingFiles) {
      try {
        await uploadFile(f, null, null, taskId);
      } catch (err) {
        toast.error(`Не удалось загрузить ${f.name}`);
      }
    }
    for (const f of pendingMedia) {
      try {
        await uploadFile(f, null, null, taskId, null, null, null, true);
      } catch (err) {
        toast.error(`Не удалось загрузить ${f.name}`);
      }
    }
  };

  const copyTaskLink = async () => {
    const url = `${window.location.origin}/tasks?task=${task.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка на задачу скопирована');
    } catch (err) {
      toast.error('Не удалось скопировать ссылку');
    }
  };

  const handleSave = async () => {
    if (canEdit && !formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (canEdit && !formData.module_id) {
      toast.error('Module is required');
      return;
    }
    setLoading(true);
    try {
      let taskId = task?.id;
      if (isEdit) {
        const payload = canEdit ? formData : { status: formData.status, checklist: formData.checklist };
        await tasksAPI.update(taskId, payload);
      } else {
        const res = await tasksAPI.create(formData);
        taskId = res.data.id;
      }
      if ((pendingFiles.length > 0 || pendingMedia.length > 0) && canEdit) {
        await uploadPending(taskId);
      }
      toast.success(isEdit ? 'Task updated' : 'Task created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task?')) return;
    setLoading(true);
    try {
      await tasksAPI.delete(task.id);
      toast.success('Task deleted');
      onSaved();
    } catch (err) {
      toast.error('Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteAttachment(attachmentId);
      toast.success('File deleted');
      onSaved();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-[600px] max-h-[95vh] sm:max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="dev-task-modal">
        <div
          className="h-1 w-full"
          style={{ backgroundColor: TASK_TYPE_COLORS[formData.task_type] || TASK_TYPE_COLORS.feature }}
        />
        <div className="flex items-center justify-between p-4 border-b border-[#E0E0E0]">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h2 className="text-lg font-semibold text-[#111111]">{isEdit ? 'Task Details' : 'New Task'}</h2>
            {isEdit && task?.task_key && (
              <div className="flex items-center gap-1">
                <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-[#101010] text-[#CFFF40]" data-testid="dev-task-key">
                  {task.task_key}
                </span>
                <button
                  type="button"
                  onClick={copyTaskLink}
                  title="Copy link to task"
                  className="p-1 rounded text-[#999999] hover:text-[#101010] hover:bg-[#F5F5F5]"
                  data-testid="copy-task-link-btn"
                >
                  <Copy size={14} />
                </button>
              </div>
            )}
            <TaskTypeBadge taskType={formData.task_type} />
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="dev-task-close-btn"><X size={20} /></Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isEdit && (task?.author_name || task?.created_at) && (
            <p className="text-xs text-[#999999]" data-testid="dev-task-author">
              Created by <span className="font-medium text-[#666666]">{task?.author_name || '—'}</span>
              {task?.created_at && <> · {new Date(task.created_at).toLocaleString()}</>}
            </p>
          )}
          <TitleDescriptionFields formData={formData} setFormData={setFormData} canEdit={canEdit} />
          <DescriptionMediaSection
            task={task}
            canEdit={canEdit}
            isAdmin={isAdmin}
            pendingMedia={pendingMedia}
            onAddMedia={(files) => setPendingMedia(p => [...p, ...files])}
            onRemovePending={(idx) => setPendingMedia(p => p.filter((_, i) => i !== idx))}
            onDeleteMedia={handleDeleteAttachment}
          />
          <TypePriorityStatusFields formData={formData} setFormData={setFormData} canEdit={canEdit} />
          <ModuleAssigneeFields formData={formData} setFormData={setFormData} canEdit={canEdit} task={task} modules={modules} users={users} />
          <RestaurantRequestedFields formData={formData} setFormData={setFormData} canEdit={canEdit} task={task} restaurants={restaurants} />
          <ChecklistEditor
            checklist={formData.checklist}
            canEdit={canEdit}
            onToggle={(id) => setChecklist(l => checklistOps.toggle(l, id))}
            onAdd={(text) => setChecklist(l => checklistOps.add(l, text))}
            onRemove={(id) => setChecklist(l => checklistOps.remove(l, id))}
            onEdit={(id, text) => setChecklist(l => checklistOps.edit(l, id, text))}
            onReorder={(fromId, toId) => setChecklist(l => checklistOps.reorder(l, fromId, toId))}
          />
          <LinkDeadlineFields formData={formData} setFormData={setFormData} canEdit={canEdit} />
          <AttachmentSection
            task={task}
            canEdit={canEdit}
            isAdmin={isAdmin}
            pendingFiles={pendingFiles}
            onAddFiles={(files) => setPendingFiles(p => [...p, ...files])}
            onRemovePending={(idx) => setPendingFiles(p => p.filter((_, i) => i !== idx))}
            onDeleteAttachment={handleDeleteAttachment}
          />
          {isEdit && (
            <ReleaseSummarySection
              taskId={task.id}
              canAdd={isAdmin || (task.assignee_ids || []).includes(currentUser?.id)}
              isAdmin={isAdmin}
              currentUser={currentUser}
            />
          )}
          {isEdit && (
            <CommentsSection
              entityId={task.id}
              getComments={tasksAPI.getComments}
              addComment={tasksAPI.addComment}
              currentUser={currentUser}
              label="Activity & Comments"
            />
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-[#E0E0E0]">
          <div>
            {isAdmin && isEdit && (
              <Button variant="destructive" onClick={handleDelete} disabled={loading} data-testid="dev-task-delete-btn">Delete</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="dev-task-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="dev-task-save-btn">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
